/**
 * Settings (TMMAPP-0012 / 0027, design §14.4; TMMAPP-0103, audit A4 §8, P:1743-1800). Eyebrow
 * "Preferences", max-width 720. TMMAPP-0119 item 23 (Operator, 2026-10-08): the tabs, in order —
 * Profile · Subscription · AI Automation · Appearance · Sharing · Notifications · Taxes — the
 * app-wide tabs (pattern 3), scrollable at `xs`, with the tab in the URL (`?tab=`, replace). An
 * older `?tab=` or `?section=` value lands on the tab that holds it now. The page reads the billing
 * profile once (`BillingProfileProvider`, TMMAPP-0112).
 *
 * TMMAPP-0161 item 2 (Operator, 2026-10-20): the Automation card moved whole from Subscription to
 * its own AI Automation tab (`?tab=ai-automation`), shown only with a profile (`allowAutomation`).
 * An older link to it (`?section=automation`, or `?tab=subscription#automation-…`) lands there.
 * TMMAPP-0162 item 2 (Operator, 2026-10-21): the tab stays visible to every tier, and for a GUEST or
 * NONE tier selecting it goes through `useSubscriberAction()` (the purchase experience, the
 * selected tab staying); a link to it opens the offer once and lands on Profile.
 *
 * A person without a subscription (never subscribed, or past grace) sees the same tabs. Sharing
 * holds the shares only when something is shared with them; its Connected AI tools card is for every tier (TMMAPP-0142 L1). Subscription
 * opens the subscribe dialog (`useSubscriberAction()`) and the selected tab stays, unless there is
 * a billing profile to show (a locked person's statements and Renew). Notifications and Taxes call
 * nothing the tier can't use: without a share to be alerted about or report on, they show an empty
 * state with SUBSCRIBE.
 *
 * TMMAPP-0151 §K (v2, prototype state 45): the prototype's lead under the title, a 980 cap, and the
 * Sharing tab opens on Virtual account sharing and Account permissions (`SharingOverview`), then
 * Connected AI tools and the shares held with the person. Money Tools' Settings entry opens that tab
 * (`?tab=sharing`, the prototype's view); a bare `/me/settings` (the account menu, the locked state)
 * still opens Profile.
 */
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { EmptyCard } from '@/components/EmptyCard';
import { HeadedCard } from '@/components/HeadedCard';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useNotify } from '@/shell/NotifyProvider';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { optionalStringParam, useUrlState } from '@/shared/useUrlState';
import { AccountTab } from './AccountTab';
import { AlertPreferencesSection } from './AlertPreferencesSection';
import { AutomationTab } from './AutomationTab';
import { BillingProfileProvider } from './BillingProfileProvider';
import { BillingTab } from './BillingTab';
import { ProfileTab } from './ProfileTab';
import {
  AppearanceSection,
  CurrencySection,
  LanguageSection,
  SettingsCard,
  SettingsSection,
  SubscriptionSummary,
} from './SettingsPreferences';
import { SharingOverview } from './SharingOverview';
import { SharingTab } from './SharingTab';
import { ConnectedAiToolsCard } from '@/features/agentTools/ConnectedAiToolsCard';
import { StartOverSection } from './StartOverSection';
import { TaxReportSection } from './TaxReportSection';
import {
  parseSettingsTab,
  SETTINGS_TABS,
  settingsPanelId,
  settingsTabId,
  type SettingsNotify,
  type SettingsTab,
} from './settingsHref';

const SCHEMA = { tab: optionalStringParam(), section: optionalStringParam() };

const TAB_LABEL_KEYS: Readonly<Record<SettingsTab, string>> = {
  profile: 'settings.tabProfile',
  subscription: 'settings.tabSubscription',
  'ai-automation': 'settings.tabAiAutomation',
  appearance: 'settings.appearance',
  sharing: 'settings.tabSharing',
  notifications: 'settings.notifications',
  taxes: 'settings.tabTaxes',
};

export function SettingsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [{ tab: rawTab, section: legacySection }, setUrl] = useUrlState(SCHEMA);
  const notifyToast = useNotify();
  const notify: SettingsNotify = (message, severity = 'success') => {
    notifyToast(message, { severity });
  };
  const title = usePageTitle({ title: t('toolsNav.settings') });

  const subscriber = me.tier === 'SUBSCRIBER' && !me.locked;
  // A subscriber's billing, or a locked person's (statements and Renew stay open, MM-D-71).
  const allowBilling = me.tier === 'SUBSCRIBER' || me.locked;
  const allowAutomation = me.user.id !== null;
  const sharedWithMe = me.virtualAccounts.some((row) => !row.mine);
  // A member's alerts and tax report cover what is shared with them; past grace they don't read.
  const memberAccess = sharedWithMe && me.user.id !== null && !(me.locked && me.tier !== 'GUEST');
  // The shares section's rule; the tab itself is for every tier (Connected AI tools, TMMAPP-0142 L1).
  const showSharing = subscriber || sharedWithMe;
  const tabs = SETTINGS_TABS.filter((tab) => tab !== 'ai-automation' || allowAutomation);

  const openSubscribe = useOpenSubscribe();
  // Every way in here is a purchase: the action and the non-subscriber path both open it.
  const subscribe = useSubscriberAction(() => openSubscribe());
  // AI Automation is a subscriber's: any other tier gets the purchase experience (TMMAPP-0162 item 2).
  const automation = useSubscriberAction(() => setUrl({ tab: 'ai-automation', section: null }));
  const automationForSale = me.tier !== 'SUBSCRIBER';

  // A link to the Automation card when it sat on Subscription (its anchor) lands on its own tab.
  const { hash } = useLocation();
  const parsed = parseSettingsTab(rawTab ?? legacySection);
  const requested =
    parsed === 'subscription' && hash.startsWith('#automation') ? 'ai-automation' : parsed;
  // A locked person's Renew lives in the management; an older `?section=subscription` link opened
  // it, and still does (read once, before the link is rewritten).
  const [manageOnArrival] = useState(() => !subscriber || legacySection === 'subscription');
  const wantsPurchase =
    (requested === 'subscription' && !allowBilling) ||
    (requested === 'ai-automation' && automationForSale);
  const current: SettingsTab =
    requested !== null && tabs.includes(requested) && !wantsPurchase ? requested : 'profile';

  // An older or unavailable value is rewritten to the tab it shows (replace, never a new entry).
  useEffect(() => {
    if (rawTab === null && legacySection === null) return;
    if (rawTab === current && legacySection === null) return;
    setUrl({ tab: current, section: null });
  }, [rawTab, legacySection, current, setUrl]);

  // A link to Subscription (Renew, a referral) for someone with nothing to manage opens the
  // purchase experience once per link, as selecting the tab does: the URL is rewritten to the tab
  // it shows, and the next link opens it again (TMMAPP-0122 item 33).
  const purchaseOpened = useRef(false);

  useEffect(() => {
    if (!wantsPurchase) {
      purchaseOpened.current = false;
      return;
    }
    if (purchaseOpened.current) return;
    purchaseOpened.current = true;
    openSubscribe();
  }, [wantsPurchase, openSubscribe]);

  function selectTab(event: SyntheticEvent, next: SettingsTab) {
    if (next === 'subscription' && !allowBilling) {
      // The purchase experience; the selected tab stays where it was.
      subscribe.run(event as MouseEvent<HTMLButtonElement>);
      return;
    }
    if (next === 'ai-automation') {
      automation.run(event as MouseEvent<HTMLButtonElement>);
      return;
    }
    setUrl({ tab: next, section: null });
  }

  const subscribeButton = (
    <Button variant="contained" onClick={subscribe.run} aria-describedby={subscribe.descriptionId}>
      {t('subscribeDialog.subscribe')}
    </Button>
  );

  let panel: ReactNode;
  switch (current) {
    case 'profile':
      panel = (
        <SettingsCard>
          <SettingsSection
            first
            id="settings-profile"
            title={t('settings.tabProfile')}
            description={t('settings.section.profile')}
          >
            <ProfileTab onNotify={notify} />
          </SettingsSection>
          <LanguageSection onNotify={notify} />
          <CurrencySection onNotify={notify} />
          {me.user.id !== null && (
            <SettingsSection id="settings-start-over" title={t('settings.startOver.title')}>
              <StartOverSection onNotify={notify} />
            </SettingsSection>
          )}
          <SettingsSection id="settings-delete-account" title={t('settings.deleteTitle')}>
            <AccountTab onNotify={notify} />
          </SettingsSection>
        </SettingsCard>
      );
      break;
    case 'subscription':
      panel = (
        <Stack sx={{ gap: 2.5 }}>
          <SettingsCard>
            <SubscriptionSummary onNotify={notify} manageOpen={manageOnArrival} />
          </SettingsCard>
          <HeadedCard
            id="settings-billing"
            title={t('settings.tabBilling')}
            subtitle={t('settings.section.billing')}
          >
            <BillingTab onNotify={notify} />
          </HeadedCard>
        </Stack>
      );
      break;
    case 'ai-automation':
      panel = (
        <HeadedCard title={t('settings.tabAutomation')} subtitle={t('settings.section.automation')}>
          <AutomationTab onNotify={notify} />
        </HeadedCard>
      );
      break;
    case 'appearance':
      panel = (
        <SettingsCard>
          <AppearanceSection />
        </SettingsCard>
      );
      break;
    case 'sharing':
      panel = (
        <Stack sx={{ gap: 2.5 }}>
          {/* TMMAPP-0151 §K 29: virtual account sharing and account permissions come first. */}
          <SharingOverview onNotify={notify} />
          {/* TMMAPP-0142 L1: free, for every tier (a connection exists without a subscription). */}
          <ConnectedAiToolsCard onNotify={notify} />
          {showSharing && (
            <SettingsCard>
              <SettingsSection
                first
                id="settings-sharing"
                title={t('settings.tabSharing')}
                description={t('settings.section.sharing')}
              >
                <SharingTab onNotify={notify} />
              </SettingsSection>
            </SettingsCard>
          )}
        </Stack>
      );
      break;
    case 'notifications':
      panel =
        subscriber || memberAccess ? (
          <SettingsCard>
            <SettingsSection
              first
              id="settings-notifications"
              title={t('settings.notifications')}
              description={t('settings.notificationsHelp')}
            >
              <AlertPreferencesSection onNotify={notify} />
            </SettingsSection>
          </SettingsCard>
        ) : (
          <EmptyCard action={subscribeButton}>{t('settings.notificationsEmpty')}</EmptyCard>
        );
      break;
    case 'taxes':
      panel =
        subscriber || memberAccess ? (
          <SettingsCard>
            <SettingsSection
              first
              id="settings-tax-report"
              title={t('settings.tabTaxReport')}
              description={t('settings.section.taxReport')}
            >
              <TaxReportSection onNotify={notify} />
            </SettingsSection>
          </SettingsCard>
        ) : (
          <EmptyCard action={subscribeButton}>{t('settings.taxReportEmpty')}</EmptyCard>
        );
      break;
  }

  // Settings is capped at 980 wide (visual contract v2 §1, P:729), under the prototype's lead.
  return (
    <BillingProfileProvider enabled={allowBilling}>
      <Box sx={{ maxWidth: 980, width: '100%' }}>
        <PageHeader title={title} subtitle={t('settings.lead')} />
        <Tabs
          value={current}
          onChange={selectTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={t('settings.tabsLabel')}
          sx={{ mt: 2 }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              id={settingsTabId(tab)}
              aria-controls={tab === current ? settingsPanelId(tab) : undefined}
              aria-describedby={tab === 'ai-automation' ? automation.descriptionId : undefined}
              label={t(TAB_LABEL_KEYS[tab])}
            />
          ))}
        </Tabs>
        <Box
          role="tabpanel"
          id={settingsPanelId(current)}
          aria-labelledby={settingsTabId(current)}
          sx={{ mt: 2.5 }}
        >
          {panel}
        </Box>
        <SubscriberActionHint id={subscribe.descriptionId} description={subscribe.description} />
        <SubscriberActionHint id={automation.descriptionId} description={automation.description} />
      </Box>
    </BillingProfileProvider>
  );
}
